import React, { useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Send, Check, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import Navbar from './Navbar';
import Footer from './Footer';
import AddressAutocomplete from './AddressAutocomplete';
import DateTimePickerField from './DateTimePickerField';
import RoutePreview from './RoutePreview';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const countries = [
  "Morocco", "Germany", "France", "United Kingdom", "Netherlands", "Belgium", 
  "Spain", "Italy", "Portugal", "Austria", "Switzerland",
  "Poland", "Czech Republic", "Denmark", "Sweden", "Norway",
  "Finland", "Ireland", "Luxembourg", "Greece", "Hungary",
  "Romania", "Bulgaria", "Croatia", "Slovakia", "Slovenia",
  "Estonia", "Latvia", "Lithuania", "Cyprus", "Malta",
  "United States", "Canada", "Australia", "Other"
];

const TripFormPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [requestId, setRequestId] = useState('');
  const [error, setError] = useState('');

  // Read signed-in customer once from sessionStorage to pre-fill contact details.
  const customer = (() => {
    try {
      const stored = sessionStorage.getItem('customer');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  })();

  // Read AI-chat pre-fills from `?pickup=&dropoff=&date=&horses=` once at mount.
  // Done in the initial `useState` so we don't trip the
  // `react-hooks/set-state-in-effect` rule.
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState(() => ({
    firstName: customer?.first_name || '',
    lastName: customer?.last_name || '',
    email: customer?.email || '',
    contactNumber: customer?.phone || '',
    country: customer?.city ? 'Morocco' : '',
    horseCount: searchParams.get('horses') || '',
    pickupAddress: searchParams.get('pickup') || '',
    dropoffAddress: searchParams.get('dropoff') || '',
    transportDate: searchParams.get('date') || '',
    additionalComments: ''
  }));
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = t('errors.firstNameRequired');
    if (!formData.lastName.trim()) newErrors.lastName = t('errors.lastNameRequired');
    if (!formData.email.trim()) newErrors.email = t('errors.emailRequired');
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = t('errors.invalidEmail');
    if (!formData.contactNumber.trim()) newErrors.contactNumber = t('errors.contactRequired');
    if (!formData.country) newErrors.country = t('errors.countryRequired');
    if (!formData.horseCount) newErrors.horseCount = t('errors.horseCountRequired');
    if (!formData.pickupAddress.trim()) newErrors.pickupAddress = t('errors.pickupRequired');
    if (!formData.dropoffAddress.trim()) newErrors.dropoffAddress = t('errors.dropoffRequired');
    if (!formData.transportDate) newErrors.transportDate = t('errors.dateRequired');
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = useCallback((name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => (prev[name] ? { ...prev, [name]: '' } : prev));
  }, []);

  const handleAddressTextChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => (prev[field] ? { ...prev, [field]: '' } : prev));
  }, []);

  const handleAddressSelect = useCallback((field, details) => {
    setFormData(prev => ({ ...prev, [field]: details.address }));
    setErrors(prev => (prev[field] ? { ...prev, [field]: '' } : prev));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      // Scroll to top so the user sees the first invalid field instead of
      // staring at a non-responsive submit button at the bottom of the page.
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      });
      return;
    }

    setIsSubmitting(true);
    setError('');
    
    try {
      const payload = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        contact_number: formData.contactNumber,
        country: formData.country,
        horse_count: formData.horseCount,
        pickup_address: formData.pickupAddress,
        dropoff_address: formData.dropoffAddress,
        transport_date: formData.transportDate,
        additional_comments: formData.additionalComments
      };
      
      const response = await axios.post(`${API}/trip-requests`, payload);
      setRequestId(response.data.request_id);
      setIsSubmitted(true);
      // Scroll to top so the user sees the success banner (the form was at
      // the bottom of the page when they clicked submit).
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      });
    } catch (err) {
      setError(err.response?.data?.detail || t('errors.submitFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <Navbar />
        <div className="pt-32 pb-24 px-4">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-8 animate-bounce">
              <Check className="w-12 h-12 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-white mb-4">{t('tripSuccess.title')}</h1>
            <p className="text-gray-400 text-lg mb-4">
              {t('tripSuccess.thankYou')}
            </p>
            
            {requestId && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-8 inline-block">
                <p className="text-gray-400 text-sm mb-1">{t('tripSuccess.requestId')}</p>
                <p className="text-amber-500 font-mono text-2xl font-bold">{requestId}</p>
              </div>
            )}
            
            <p className="text-gray-400 text-sm mb-8">
              {t('tripSuccess.confirmationEmail')}
            </p>
            
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 mb-8">
              <h3 className="text-white font-semibold mb-4">{t('tripSuccess.whatNext')}</h3>
              <ul className="text-left text-gray-400 space-y-3">
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-amber-500 text-black rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold">1</span>
                  <span>{t('tripSuccess.step1')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-amber-500 text-black rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold">2</span>
                  <span>{t('tripSuccess.step2')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-amber-500 text-black rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold">3</span>
                  <span>{t('tripSuccess.step3')}</span>
                </li>
              </ul>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/')}
                className="bg-amber-500 hover:bg-amber-600 text-black font-bold px-8 py-6 rounded-full"
              >
                {t('common.backToHome')}
              </Button>
              <Button
                onClick={() => navigate(`/my-quotes?request_id=${requestId}&email=${formData.email}`)}
                variant="outline"
                className="border-amber-500/50 text-amber-500 hover:bg-amber-500/10 px-8 py-6 rounded-full"
              >
                {t('tripSuccess.viewMyQuotes')}
              </Button>
              <Button
                onClick={() => {
                  setIsSubmitted(false);
                  setRequestId('');
                  setFormData({
                    firstName: customer?.first_name || '',
                    lastName: customer?.last_name || '',
                    email: customer?.email || '',
                    contactNumber: customer?.phone || '',
                    country: customer?.city ? 'Morocco' : '',
                    horseCount: '',
                    pickupAddress: '',
                    dropoffAddress: '',
                    transportDate: '',
                    additionalComments: ''
                  });
                }}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10 px-8 py-6 rounded-full"
              >
                {t('tripSuccess.submitAnother')}
              </Button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-amber-900/20 via-transparent to-teal-900/20" />
          <div className="absolute top-1/4 right-0 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Back button */}
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-400 hover:text-amber-500 transition-colors mb-8"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>{t('common.backToHome')}</span>
          </button>

          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
              {t('tripForm.pageTitle').split(' ').slice(0, 2).join(' ')} <span className="text-amber-500">{t('tripForm.pageTitle').split(' ').slice(2).join(' ')}</span>
            </h1>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              {t('tripForm.pageSubtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="pb-24 px-4">
        <div className="max-w-4xl mx-auto">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6 text-center">
              <p className="text-red-500">{error}</p>
            </div>
          )}
          <form onSubmit={handleSubmit} className="bg-gradient-to-br from-white/5 to-transparent border border-white/10 rounded-3xl p-8 md:p-12">
            {/* Name Fields */}
            <div className="mb-8">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-amber-500 text-black rounded-full flex items-center justify-center text-sm font-bold">1</span>
                {t('tripForm.contactInfo')}
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="firstName" className="text-gray-300 mb-2 block">
                    {t('tripForm.firstName')} <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder={t('tripForm.firstName')}
                    className={`bg-black/30 border-white/10 text-white placeholder:text-gray-500 h-12 rounded-xl focus:border-amber-500 focus:ring-amber-500/20 ${errors.firstName ? 'border-red-500' : ''}`}
                  />
                  {errors.firstName && <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>}
                </div>
                <div>
                  <Label htmlFor="lastName" className="text-gray-300 mb-2 block">
                    {t('tripForm.lastName')} <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    placeholder={t('tripForm.lastName')}
                    className={`bg-black/30 border-white/10 text-white placeholder:text-gray-500 h-12 rounded-xl focus:border-amber-500 focus:ring-amber-500/20 ${errors.lastName ? 'border-red-500' : ''}`}
                  />
                  {errors.lastName && <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>}
                </div>
              </div>
            </div>

            {/* Contact Fields */}
            <div className="mb-8">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="email" className="text-gray-300 mb-2 block">
                    {t('tripForm.email')} <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="your@email.com"
                    className={`bg-black/30 border-white/10 text-white placeholder:text-gray-500 h-12 rounded-xl focus:border-amber-500 focus:ring-amber-500/20 ${errors.email ? 'border-red-500' : ''}`}
                  />
                  {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                </div>
                <div>
                  <Label htmlFor="contactNumber" className="text-gray-300 mb-2 block">
                    {t('tripForm.contactNumber')} <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="contactNumber"
                    name="contactNumber"
                    type="tel"
                    value={formData.contactNumber}
                    onChange={handleInputChange}
                    placeholder="+49 123 456 7890"
                    className={`bg-black/30 border-white/10 text-white placeholder:text-gray-500 h-12 rounded-xl focus:border-amber-500 focus:ring-amber-500/20 ${errors.contactNumber ? 'border-red-500' : ''}`}
                  />
                  {errors.contactNumber && <p className="text-red-500 text-sm mt-1">{errors.contactNumber}</p>}
                </div>
              </div>
            </div>

            {/* Country */}
            <div className="mb-8">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="country" className="text-gray-300 mb-2 block">
                    {t('tripForm.country')} <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.country}
                    onValueChange={(value) => handleSelectChange('country', value)}
                  >
                    <SelectTrigger className={`bg-black/30 border-white/10 text-white h-12 rounded-xl focus:border-amber-500 focus:ring-amber-500/20 ${errors.country ? 'border-red-500' : ''}`}>
                      <SelectValue placeholder={t('tripForm.selectCountry')} />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a1a] border-white/10">
                      {countries.map((country) => (
                        <SelectItem key={country} value={country} className="text-white hover:bg-amber-500/20">
                          {country}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.country && <p className="text-red-500 text-sm mt-1">{errors.country}</p>}
                </div>
              </div>
            </div>

            {/* Trip Details */}
            <div className="mb-8">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-amber-500 text-black rounded-full flex items-center justify-center text-sm font-bold">2</span>
                {t('tripForm.tripDetails')}
              </h3>

              {/* Horse Count — moved here from contact section */}
              <div className="mb-6">
                <Label htmlFor="horseCount" className="text-gray-300 mb-2 block">
                  {t('tripForm.horseCount')} <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.horseCount}
                  onValueChange={(value) => handleSelectChange('horseCount', value)}
                >
                  <SelectTrigger className={`bg-black/30 border-white/10 text-white h-12 rounded-xl focus:border-amber-500 focus:ring-amber-500/20 max-w-xs ${errors.horseCount ? 'border-red-500' : ''}`} data-testid="horse-count-select">
                    <SelectValue placeholder={t('tripForm.selectHorses')} />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a1a] border-white/10">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, '10+'].map((num) => (
                      <SelectItem key={num} value={String(num)} className="text-white hover:bg-amber-500/20">
                        {num} {num === 1 ? t('tripForm.horse') : t('tripForm.horses')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.horseCount && <p className="text-red-500 text-sm mt-1">{errors.horseCount}</p>}
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <Label htmlFor="pickupAddress" className="text-gray-300 mb-2 block">
                    {t('tripForm.pickupAddress')} <span className="text-red-500">*</span>
                  </Label>
                  <AddressAutocomplete
                    id="pickupAddress"
                    value={formData.pickupAddress}
                    onChange={(v) => handleAddressTextChange('pickupAddress', v)}
                    onSelect={(details) => handleAddressSelect('pickupAddress', details)}
                    placeholder={t('tripForm.addressPlaceholder')}
                    country="ma"
                    hasError={!!errors.pickupAddress}
                    testIdPrefix="pickup-address"
                  />
                  {errors.pickupAddress && <p className="text-red-500 text-sm mt-1">{errors.pickupAddress}</p>}
                </div>
                <div>
                  <Label htmlFor="dropoffAddress" className="text-gray-300 mb-2 block">
                    {t('tripForm.dropoffAddress')} <span className="text-red-500">*</span>
                  </Label>
                  <AddressAutocomplete
                    id="dropoffAddress"
                    value={formData.dropoffAddress}
                    onChange={(v) => handleAddressTextChange('dropoffAddress', v)}
                    onSelect={(details) => handleAddressSelect('dropoffAddress', details)}
                    placeholder={t('tripForm.addressPlaceholder')}
                    country="ma"
                    hasError={!!errors.dropoffAddress}
                    testIdPrefix="dropoff-address"
                  />
                  {errors.dropoffAddress && <p className="text-red-500 text-sm mt-1">{errors.dropoffAddress}</p>}
                </div>
              </div>

              {/* Route preview: static map + distance + duration. Lazily fetched once both addresses are set. */}
              <RoutePreview
                pickupAddress={formData.pickupAddress}
                dropoffAddress={formData.dropoffAddress}
              />

              <div>
                <Label htmlFor="transportDate" className="text-gray-300 mb-2 block">
                  {t('tripForm.transportDate')} <span className="text-red-500">*</span>
                </Label>
                <DateTimePickerField
                  value={formData.transportDate}
                  onChange={(v) => {
                    setFormData((prev) => ({ ...prev, transportDate: v }));
                    setErrors((prev) => (prev.transportDate ? { ...prev, transportDate: '' } : prev));
                  }}
                  hasError={!!errors.transportDate}
                />
                <p className="text-gray-500 text-xs mt-2">Choose the pickup date and approximate time.</p>
                {errors.transportDate && <p className="text-red-500 text-sm mt-1">{errors.transportDate}</p>}
              </div>
            </div>

            {/* Additional Comments */}
            <div className="mb-8">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-amber-500 text-black rounded-full flex items-center justify-center text-sm font-bold">3</span>
                {t('tripForm.additionalInfo')}
              </h3>
              <Label htmlFor="additionalComments" className="text-gray-300 mb-2 block">
                {t('tripForm.additionalComments')} ({t('common.optional')})
              </Label>
              <Textarea
                id="additionalComments"
                name="additionalComments"
                value={formData.additionalComments}
                onChange={handleInputChange}
                placeholder={t('tripForm.commentsPlaceholder')}
                rows={4}
                className="bg-black/30 border-white/10 text-white placeholder:text-gray-500 rounded-xl resize-none focus:border-amber-500 focus:ring-amber-500/20"
              />
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold py-6 rounded-full text-lg transition-all duration-300 hover:scale-[1.02] disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  {t('tripForm.submitting')}
                </>
              ) : (
                <>
                  <Send className="w-5 h-5 mr-2" />
                  {t('tripForm.submitRequest')}
                </>
              )}
            </Button>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TripFormPage;
